import type { ApolloError } from "@apollo/client";
import { useCallback } from "react";
import { useRouter } from "solito/router";
import { v4 as uuid } from "uuid";
import type { GetPersonalizedPromptsQuery } from "@medbillai/graphql-types";
import { ScrollView, Sparkles, XStack } from "@medbillai/ui";
import type { CaseType } from "@medbillai/schemas";
import { issuePreviewUrl } from "@medbillai/utils";
import { analyticsService } from "../../../lib/analytics";
import { useCaseStore } from "../../../lib/caseStore";
import { encodePreviewConfig } from "../../case/hooks/useCaseScreenParams";
import { createSystemMessageEvent, createUserChatEvent } from "../../case/utils/createPreviewEvents";
import { PersonalizedPrompt } from "./PersonalizedPrompt";
import { WidgetWrapper } from "./WidgetWrapper";
type PersonalizedPromptsProps = {
  prompts: NonNullable<GetPersonalizedPromptsQuery["getPersonalizedPrompts"]>["prompts"];
  error: ApolloError | undefined;
};
export function PersonalizedPrompts({
  prompts,
  error
}: PersonalizedPromptsProps) {
  const {
    push
  } = useRouter();
  const setPreviewData = useCaseStore(state => state.setPreviewData);
  const handlePromptPress = useCallback((prompt: GetPersonalizedPromptsQuery["getPersonalizedPrompts"]["prompts"][number]) => {
    const {
      id,
      text,
      response,
      caseType
    } = prompt;
    analyticsService.trackEvent("CaseIntakeStarted", {
      source: `personalized_prompt: ${text}`
    });

    // Generate storeKey and create preview data
    const storeKey = uuid();
    const events = [{
      event: createUserChatEvent(text),
      metadata: {} // USER message - no messageId needed
    }, {
      event: createSystemMessageEvent(response),
      metadata: {
        messageId: id
      } // SYSTEM message with messageId
    }];

    // Save data to store
    setPreviewData(storeKey, {
      events
    });

    // Navigate to preview case
    push(issuePreviewUrl({
      config: encodePreviewConfig({
        id: storeKey,
        caseType: caseType as CaseType,
        title: text,
        events: events
      })
    }));
  }, [push, setPreviewData]);
  if (error || prompts.length < 1) {
    return null;
  }
  return <WidgetWrapper title="Ask Granted" titleIcon={Sparkles} data-sentry-element="WidgetWrapper" data-sentry-component="PersonalizedPrompts" data-sentry-source-file="PersonalizedPrompts.tsx">
      <ScrollView horizontal showsHorizontalScrollIndicator={false} mx="$-4" data-sentry-element="ScrollView" data-sentry-source-file="PersonalizedPrompts.tsx">
        <XStack gap="$1.5" px="$4" data-sentry-element="XStack" data-sentry-source-file="PersonalizedPrompts.tsx">
          {prompts.map(prompt => <PersonalizedPrompt key={prompt.id} prompt={prompt} onPress={() => handlePromptPress(prompt)} />)}
        </XStack>
      </ScrollView>
    </WidgetWrapper>;
}